import { Image } from "@/components/ui/image";
import { HoverPrefetchLink } from "@/components/ui/hover-prefetch-link";
import type { ProductCardDisplay } from "./product-card-display";
import { ProductCardPlaceholder } from "./product-card-placeholder";

const ROW_IMAGE_SIZES = "(min-width: 768px) 176px, 112px";

function RowPhoto({ display, priority }: { display: ProductCardDisplay; priority: boolean }) {
  return (
    <div className="relative aspect-[4/3] w-28 shrink-0 overflow-hidden rounded-xl bg-surface-2 @xl:w-44">
      {display.imageUrl ? (
        <Image
          src={display.imageUrl}
          alt={display.name || "Listing photo"}
          fill
          className="object-cover"
          sizes={ROW_IMAGE_SIZES}
          quality={60}
          loading={priority ? "eager" : undefined}
          fetchPriority={priority ? "high" : undefined}
        />
      ) : (
        <ProductCardPlaceholder productTypeSlug={display.productTypeSlug} />
      )}
    </div>
  );
}

function RowPrice({ display }: { display: ProductCardDisplay }) {
  if (display.freeLabel) {
    return <span className="text-lg font-semibold text-success-strong @xl:text-2xl">{display.freeLabel}</span>;
  }
  if (!display.unitPrice) {
    return <span className="text-base text-fg-muted">Price on request</span>;
  }
  return (
    <span className="flex flex-wrap items-baseline gap-x-1.5 @xl:justify-end">
      <span className="whitespace-nowrap text-lg font-semibold tracking-tight tabular-nums text-foreground @xl:text-2xl">
        ${display.unitPrice}
      </span>
      <span className="text-[0.9375rem] text-fg-muted">/ {display.unitName} at pickup</span>
    </span>
  );
}

function availabilityLine(display: ProductCardDisplay): string | null {
  const parts = [
    display.quantityAvailable && display.quantityAvailable > 0
      ? `${display.quantityAvailable.toLocaleString("en-US")} available`
      : null,
    display.minimumOrder,
    display.pickupOnly ? "Pickup only" : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function ProductListingRow({ display, priority = false }: { display: ProductCardDisplay; priority?: boolean }) {
  const spec = [display.grade, display.dimensions].filter(Boolean).join(" · ");
  const availability = availabilityLine(display);
  return (
    <article className="group relative flex gap-4 py-5 @xl:gap-6 @xl:py-6 has-[[data-row-link]:focus-visible]:rounded-xl has-[[data-row-link]:focus-visible]:ring-3 has-[[data-row-link]:focus-visible]:ring-ring">
      <RowPhoto display={display} priority={priority} />
      <div className="flex min-w-0 flex-1 flex-col gap-1 @xl:flex-row @xl:gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <HoverPrefetchLink
            href={display.href}
            data-row-link
            className="text-[1.0625rem] leading-snug font-semibold text-foreground outline-none after:absolute after:inset-0 after:content-[''] group-hover:underline @xl:text-lg"
          >
            {display.name}
          </HoverPrefetchLink>
          {spec ? <p className="text-[0.9375rem] text-fg-muted @xl:text-base">{spec}</p> : null}
          <p className="text-[0.9375rem] text-foreground @xl:text-base">{display.location || "Location unavailable"}</p>
          {availability ? <p className="text-[0.9375rem] text-fg-muted">{availability}</p> : null}
        </div>
        <div className="@xl:shrink-0 @xl:text-right">
          <RowPrice display={display} />
        </div>
      </div>
    </article>
  );
}
